var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { z } from "zod";
import { isValidSmsNumber } from "../../functions/smsNumber";
import { ValidationMethods } from "../utility/validationMethods";
var hasUppercaseLetter = function (value) {
    for (var index = 0; index < value.length; index += 1) {
        var code = value.charCodeAt(index);
        if (code >= 65 && code <= 90) {
            return true;
        }
    }
    return false;
};
var hasNumericCharacter = function (value) {
    for (var index = 0; index < value.length; index += 1) {
        var code = value.charCodeAt(index);
        if (code >= 48 && code <= 57) {
            return true;
        }
    }
    return false;
};
var hasSpecialCharacter = function (value) {
    for (var index = 0; index < value.length; index += 1) {
        var code = value.charCodeAt(index);
        var isUpper = code >= 65 && code <= 90;
        var isLower = code >= 97 && code <= 122;
        var isNumber = code >= 48 && code <= 57;
        if (!isUpper && !isLower && !isNumber) {
            return true;
        }
    }
    return false;
};
var isAlphaNumericWithSpaces = function (value) {
    for (var index = 0; index < value.length; index += 1) {
        var code = value.charCodeAt(index);
        var isUpper = code >= 65 && code <= 90;
        var isLower = code >= 97 && code <= 122;
        var isNumber = code >= 48 && code <= 57;
        var isSpace = code === 32;
        if (!isUpper && !isLower && !isNumber && !isSpace) {
            return false;
        }
    }
    return true;
};
var guestDetailsSchemaBase = z.object({
    guestFirstName: z.string().trim().min(2, "Please enter a valid first name.")
        .refine(function (value) { return ValidationMethods.isNotEmoji(value); }, "This field does not support emojis")
        .refine(function (value) { return ValidationMethods.isValidName(value); }, "First name can only include letters, spaces, periods, apostrophes, and hyphens."),
    guestLastName: z.string().trim().min(2, "Please enter a valid last name.")
        .refine(function (value) { return ValidationMethods.isNotEmoji(value); }, "This field does not support emojis")
        .refine(function (value) { return ValidationMethods.isValidName(value); }, "Last name can only include letters, spaces, periods, apostrophes, and hyphens."),
    guestEmail: z.string().trim()
        .refine(function (value) { return ValidationMethods.isNotEmoji(value); }, "This field does not support emojis.")
        .email("Please enter a valid email address."),
    guestPhone: z.string().trim()
        .refine(function (value) { return ValidationMethods.isNotEmoji(value); }, "This field does not support emojis")
        .refine(function (value) { return isValidSmsNumber(value) && ValidationMethods.hasValidPhoneAreaCode(value); }, "Please enter a valid phone number."),
});
var verticalCheckoutSchemaBase = z.object(__assign(__assign({ manualTipInput: z.string().refine(function (value) {
        if (value.length === 0) {
            return true;
        }
        if (value.length > 6) {
            return false;
        }
        var decimalCount = 0;
        var digitsAfterDecimal = 0;
        for (var index = 0; index < value.length; index += 1) {
            var char = value[index];
            if (char === ".") {
                decimalCount += 1;
                if (decimalCount > 1) {
                    return false;
                }
                continue;
            }
            var code = value.charCodeAt(index);
            if (code < 48 || code > 57) {
                return false;
            }
            if (decimalCount === 1) {
                digitsAfterDecimal += 1;
                if (digitsAfterDecimal > 2) {
                    return false;
                }
            }
        }
        return true;
    }, "Tip must be a valid dollar amount."), orderComment: z.string()
        .trim()
        .refine(function (value) { return ValidationMethods.isNotEmoji(value); }, "This field does not support emojis")
        .max(40, "Comment must be 40 characters or fewer."), smsNumber: z.string().trim().refine(function (value) {
        if (!value) {
            return true;
        }
        return isValidSmsNumber(value) && ValidationMethods.hasValidPhoneAreaCode(value);
    }, "Please enter a valid SMS phone number.") }, guestDetailsSchemaBase.shape), { guestPassword: z.string().optional(), guestConfirmPassword: z.string().optional() }));
export var guestDetailsSchema = guestDetailsSchemaBase;
export var verticalCheckoutSchema = verticalCheckoutSchemaBase.superRefine(function (value, ctx) {
    var password = String(value.guestPassword || "");
    if (!password) {
        return;
    }
    if (password.length < 8) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["guestPassword"],
            message: "Password must be at least 8 characters long.",
        });
    }
    if (!hasUppercaseLetter(password)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["guestPassword"],
            message: "Password must include at least one uppercase letter.",
        });
    }
    if (!hasNumericCharacter(password)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["guestPassword"],
            message: "Password must include at least one number.",
        });
    }
    if (!hasSpecialCharacter(password)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["guestPassword"],
            message: "Password must include at least one special character.",
        });
    }
    if (String(value.guestConfirmPassword || "") !== password) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["guestConfirmPassword"],
            message: "Passwords must match.",
        });
    }
});
